import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import type { HydratedTripLineItem } from 'src/features/billing/invoices/forms/cells/types';
import { INVOICE_LINE_ITEM_SCROLL_CONTAINER_ID } from 'src/features/billing/invoices/forms/constants';
import { editLineItems } from 'src/features/billing/invoices/invoice.slice';
import type { HydratedInvoice } from 'src/features/billing/invoices/useHydratedInvoice';

/**
 * Scrolls a line item group into view *within the invoice modal's scroll
 * container* rather than scrolling the whole document (which is what
 * Element.scrollIntoView would do).
 */
function scrollLineItemGroupIntoView(tripId: string) {
  const container = document.getElementById(
    INVOICE_LINE_ITEM_SCROLL_CONTAINER_ID
  );
  const anchor = document.getElementById(`scroll-anchor-${tripId}`);
  if (!container || !anchor) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  container.scrollTo({
    top: container.scrollTop + (anchorRect.top - containerRect.top),
    behavior: 'smooth',
  });
}

const isMissingDescription = (item: HydratedTripLineItem) =>
  item.line_item_type === 'modifier' && !item.description.trim();

/**
 * Validates that every persisted invoice line item has a non-blank
 * description. Blank descriptions are rejected by the backend, so we catch
 * them on submit: mark every offending field with a form error and open +
 * scroll to the first invalid line item group. Returns false when any
 * description is blank (submit should be aborted), true otherwise.
 */
export function useValidateLineItemDescriptions() {
  const { getValues, setError } = useFormContext<HydratedInvoice>();
  const dispatch = useAppDispatch();

  return useCallback(() => {
    const lineItems = getValues('line_items');

    lineItems.forEach((item, index) => {
      if (isMissingDescription(item)) {
        setError(`line_items.${index}.description`, {
          type: 'required',
          message: 'Description is required',
        });
      }
    });

    const firstInvalidItem = lineItems.find(isMissingDescription);

    const firstInvalidTripId = (
      firstInvalidItem?.trip as { id?: string } | undefined
    )?.id;
    if (firstInvalidTripId) {
      // open the offending group so the error is visible
      dispatch(editLineItems(firstInvalidTripId));
      setTimeout(() => {
        scrollLineItemGroupIntoView(firstInvalidTripId);
      }, 100);
    }

    return firstInvalidItem === undefined;
  }, [getValues, setError, dispatch]);
}
